/**
 * Checkout — postcode → city auto-fill.
 *
 * UX cel: user wpisuje kod pocztowy w formacie XX-XXX → city auto-fills "w locie"
 * (debounced 250 ms). User NIE musi wpisywać miasta. Działa dla billing + shipping.
 *
 * Strategia danych: prefix-based lookup (pierwsze 2 cyfry = region) + exact overrides.
 * Pokrywa wszystkie główne aglomeracje PL (Warszawa, Kraków, Wrocław, Poznań, Gdańsk,
 * Łódź, Katowice, Szczecin, Bydgoszcz, Lublin, Kielce, etc.) bez zewnętrznego API.
 *
 * Konwencje:
 * - Jeśli user wpisał ręcznie miasto przed kodem pocztowym → nie nadpisujemy.
 * - Po fill: trigger `change` na #city → istniejący checkout-shipping-refresh.js
 *   wywołuje REST update_order_review (shipping cost recalc).
 * - Jeśli user potem zmieni miasto ręcznie → flag `data-hn-co-city-user-typed=1`,
 *   żaden kolejny postcode lookup go nie nadpisze.
 */

/* Prefix map: pierwsze 2 cyfry kodu → miasto. High confidence (95%+ kodów w danym
 * zakresie należy do tego miasta — bezpieczne dla aggressive auto-fill). */
const POSTCODE_PREFIX = {
    // Mazowieckie / centralna PL
    '00': 'Warszawa', '01': 'Warszawa', '02': 'Warszawa', '03': 'Warszawa', '04': 'Warszawa',
    '08': 'Siedlce', '09': 'Płock',
    // Warmińsko-mazurskie / Podlaskie
    '10': 'Olsztyn', '11': 'Olsztyn',
    '15': 'Białystok', '16': 'Augustów', '17': 'Bielsk Podlaski', '18': 'Łomża',
    // Lubelskie / Świętokrzyskie
    '20': 'Lublin', '21': 'Świdnik', '22': 'Zamość', '24': 'Puławy',
    '25': 'Kielce', '26': 'Skarżysko-Kamienna', '27': 'Sandomierz',
    // Małopolskie / Podkarpackie
    '30': 'Kraków', '31': 'Kraków', '33': 'Tarnów', '34': 'Nowy Sącz',
    '35': 'Rzeszów', '37': 'Przemyśl', '38': 'Krosno',
    // Śląskie
    '40': 'Katowice', '41': 'Sosnowiec', '42': 'Częstochowa', '43': 'Bielsko-Biała',
    '44': 'Gliwice',
    // Opolskie
    '45': 'Opole', '47': 'Racibórz', '48': 'Nysa',
    // Dolnośląskie
    '50': 'Wrocław', '51': 'Wrocław', '52': 'Wrocław', '53': 'Wrocław', '54': 'Wrocław',
    '58': 'Wałbrzych', '59': 'Legnica',
    // Wielkopolskie
    '60': 'Poznań', '61': 'Poznań', '62': 'Konin', '63': 'Kalisz', '64': 'Leszno',
    // Lubuskie
    '65': 'Zielona Góra', '66': 'Gorzów Wielkopolski', '68': 'Żary',
    // Zachodniopomorskie
    '70': 'Szczecin', '71': 'Szczecin', '73': 'Stargard',
    '75': 'Koszalin', '76': 'Słupsk',
    // Pomorskie
    '80': 'Gdańsk', '81': 'Gdynia', '82': 'Tczew', '83': 'Kartuzy', '84': 'Wejherowo',
    // Kujawsko-pomorskie
    '85': 'Bydgoszcz', '86': 'Grudziądz', '87': 'Toruń', '88': 'Inowrocław',
    // Łódzkie
    '90': 'Łódź', '91': 'Łódź', '92': 'Łódź', '93': 'Łódź', '94': 'Łódź',
    '95': 'Pabianice', '96': 'Skierniewice', '97': 'Piotrków Trybunalski',
    '98': 'Sieradz', '99': 'Kutno',
};

/* Exact match overrides — gdy prefix daje błędne miasto (np. 32-XXX to mix Wieliczka /
 * Bochnia / suburbs Krakowa, nie warto zgadywać prefiksem). Tu można dorzucać konkretne
 * miasta w czasie eksploatacji. */
const POSTCODE_EXACT = {
    // Konkretne wpisy do dorzucenia jeśli klient zgłosi że dla X-Y-Z miasto się nie wypełnia.
    // Format: 'XX-XXX': 'Miasto',
};

const POSTCODE_REGEX = /^[0-9]{2}-[0-9]{3}$/;
const DEBOUNCE_MS = 250;

/**
 * Lookup miasta dla kodu pocztowego.
 * @param {string} code Kod w formacie XX-XXX.
 * @returns {string|null} Miasto lub null jeśli brak w mapie.
 */
function lookupCity(code) {
    if (!POSTCODE_REGEX.test(code)) return null;
    if (POSTCODE_EXACT[code]) return POSTCODE_EXACT[code];
    const prefix = code.substring(0, 2);
    return POSTCODE_PREFIX[prefix] || null;
}

/**
 * Format kodu pocztowego: bierzemy tylko cyfry, max 5, wstawiamy myślnik po 2-iej.
 * "81577" → "81-577", "81-577" → "81-577", "81 577" → "81-577", "abc81577xyz" → "81-577".
 * @param {string} raw Wartość z inputa.
 * @returns {string} Sformatowany kod (0-6 znaków).
 */
function formatPostcode(raw) {
    const digits = (raw || '').replace(/\D/g, '').slice(0, 5);
    if (digits.length <= 2) return digits;
    return digits.slice(0, 2) + '-' + digits.slice(2);
}

/**
 * Wire'uje pair postcode → city: postcode input fires lookup, city input
 * tracks "user typed" state żeby nigdy nie nadpisać ręcznego wpisu.
 *
 * @param {string} postcodeId DOM ID kodu (np. 'billing_postcode' / 'shipping_postcode').
 * @param {string} cityId     DOM ID miasta (np. 'billing_city' / 'shipping_city').
 */
function wirePair(postcodeId, cityId) {
    const postcodeEl = document.getElementById(postcodeId);
    const cityEl = document.getElementById(cityId);
    if (!postcodeEl || !cityEl) return;

    /* Jeśli user już ma wartość w mieście przy załadowaniu (np. zalogowany customer
     * z zapisanym adresem) — traktuj jako "user typed" żeby nie nadpisać. */
    if (cityEl.value.trim() !== '') {
        cityEl.dataset.hnCoCityUserTyped = '1';
    }

    /* Każde ręczne wpisanie w city blokuje przyszłe auto-fill. Reset gdy user
     * wyczyści pole — wtedy znów otwieramy się na auto-fill. */
    cityEl.addEventListener('input', () => {
        if (cityEl.value.trim() === '') {
            delete cityEl.dataset.hnCoCityUserTyped;
        } else {
            cityEl.dataset.hnCoCityUserTyped = '1';
        }
    });

    let timer = null;
    const tryFill = () => {
        const code = postcodeEl.value.trim();
        if (!POSTCODE_REGEX.test(code)) return;
        if (cityEl.dataset.hnCoCityUserTyped === '1') return; // User own input — nie nadpisuj.

        const city = lookupCity(code);
        if (!city) return;
        if (cityEl.value === city) return;

        cityEl.value = city;
        /* Trigger change + input — `checkout-shipping-refresh.js` słucha na change/input
         * billing_postcode/city żeby zrefreshować shipping cost przez REST. */
        cityEl.dispatchEvent(new Event('input', { bubbles: true }));
        cityEl.dispatchEvent(new Event('change', { bubbles: true }));
    };

    /**
     * Live format na każde wpisanie znaku. Auto-insert myślnika po 2-iej cyfrze,
     * strip non-digits. Caret jump'uje na koniec — typowy UX dla zip mask'ów.
     * Skip jeśli user właśnie usuwa znak (Backspace na pozycji 3 = "81-" → "81")
     * — bez tego user nie może wyczyścić myślnika.
     */
    postcodeEl.addEventListener('input', (e) => {
        const isDeletion = e.inputType && (e.inputType === 'deleteContentBackward' || e.inputType === 'deleteContentForward');
        const formatted = formatPostcode(postcodeEl.value);
        if (!isDeletion && formatted !== postcodeEl.value) {
            postcodeEl.value = formatted;
            try { postcodeEl.setSelectionRange(formatted.length, formatted.length); } catch (_) { /* noop */ }
        }

        clearTimeout(timer);
        timer = setTimeout(tryFill, DEBOUNCE_MS);
    });

    /* Blur — wymusza format (np. przy paste "81 577" lub browser autofill "81577")
     * + odpala lookup gdy user tabuje przed debounce timerem. */
    postcodeEl.addEventListener('blur', () => {
        clearTimeout(timer);
        const formatted = formatPostcode(postcodeEl.value);
        if (formatted !== postcodeEl.value) postcodeEl.value = formatted;
        tryFill();
    });
}

/**
 * Init — billing + shipping pair, plus re-wire jeśli shipping fields renderują
 * się dynamicznie (toggle "Wyślij na inny adres").
 */
export function initCheckoutPostcodeLookup() {
    /* Czekamy aż DOM ma sekcję checkout — guard przed odpalaniem na innych stronach. */
    if (!document.querySelector('[data-hn-co-postcode]') && !document.getElementById('billing_postcode')) {
        return;
    }

    wirePair('billing_postcode', 'billing_city');
    wirePair('shipping_postcode', 'shipping_city');

    /* `ship_to_different_address` toggle ujawnia/ukrywa shipping fields — re-wire na każde
     * pokazanie (idempotent: wirePair sprawdza istnienie elementu). */
    const shipToggle = document.getElementById('ship_to_different_address');
    if (shipToggle) {
        shipToggle.addEventListener('change', () => {
            wirePair('shipping_postcode', 'shipping_city');
        });
    }
}
